/**
 * @fileoverview Functions for interacting with the Teller contract
 */
import type { Address } from "viem";
import { TellerAbi } from "../contracts/teller-abi";
import { getClient } from "../lib/client";
import type { ChainId } from "./vault-config";

/**
 * Arguments required by the bridge contract for cross-chain transfers
 * @interface BridgeData
 * @property {number} chainSelector - Unique identifier for the destination chain
 * @property {Address} destinationChainReceiver - Address that will receive the bridged tokens
 * @property {Address} bridgeFeeToken - Token used to pay the bridge fee
 * @property {bigint} messageGas - Amount of gas allocated for the cross-chain message
 * @property {`0x${string}`} data - Additional data required for the bridge operation (hex encoded)
 */
export type BridgeData = {
  chainSelector: number;
  destinationChainReceiver: Address;
  bridgeFeeToken: Address;
  messageGas: bigint;
  data: `0x${string}`;
};

type GetPreviewFeeProps = {
  shareAmount: bigint;
  bridgeData: BridgeData;
  contractAddress: Address;
  chainId: ChainId;
};

const getPreviewFee = async ({
  shareAmount,
  bridgeData,
  contractAddress,
  chainId,
}: GetPreviewFeeProps): Promise<bigint> => {
  const client = await getClient(chainId);

  const rate = await client.readContract({
    abi: TellerAbi,
    address: contractAddress,
    functionName: "previewFee",
    args: [shareAmount, bridgeData],
  });

  return rate;
};

export { getPreviewFee };
